import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { Plus, Edit, Trash2, Eye, EyeOff, Mail, Settings } from 'lucide-react';
import { api } from '@/lib/api';
import { toast } from 'react-hot-toast';
import { formatPhone, formatCNPJ } from '@/lib/formatters';

interface Company {
  id: string;
  name: string;
  document: string;
  coderp?: string;
  email: string;
  phone: string;
  address: string;
  // Configuração de Email
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPassword?: string;
  smtpSecure?: boolean;
  smtpFromName?: string;
  smtpFromEmail?: string;
  isDefault?: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface CreateCompanyData {
  name: string;
  document: string;
  coderp?: string;
  email: string;
  phone: string;
  address: string;
  // Configuração de Email
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPassword?: string;
  smtpSecure?: boolean;
  smtpFromName?: string;
  smtpFromEmail?: string;
  isDefault?: boolean;
}

interface UpdateCompanyData {
  name?: string;
  document?: string;
  coderp?: string;
  email?: string;
  phone?: string;
  address?: string;
  // Configuração de Email
  smtpHost?: string;
  smtpPort?: number;
  smtpUser?: string;
  smtpPassword?: string;
  smtpSecure?: boolean;
  smtpFromName?: string;
  smtpFromEmail?: string;
  isDefault?: boolean;
}

export const CompaniesPage: React.FC = () => {
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState<CreateCompanyData>({
    name: '',
    document: '',
    email: '',
    phone: '',
    address: '',
    // Configuração de Email
    smtpHost: '',
    smtpPort: 587,
    smtpUser: '',
    smtpPassword: '',
    smtpSecure: false,
    smtpFromName: '',
    smtpFromEmail: '',
    isDefault: false,
  });

  const loadCompanies = async () => {
    try {
      setLoading(true);
      const response = await api.get('/companies');
      setCompanies(response.data.data.companies);
    } catch (error) {
      toast.error('Erro ao carregar empresas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

    const handleCreateCompany = async () => {
    try {
      await api.post('/companies', formData);
      toast.success('Empresa criada com sucesso');
      setShowCreateModal(false);
      setFormData({
        name: '',
        document: '',
        email: '',
        phone: '',
        address: '',
        smtpHost: '',
        smtpPort: 587,
        smtpUser: '',
        smtpPassword: '',
        smtpSecure: false,
        smtpFromName: '',
        smtpFromEmail: '',
      });
      loadCompanies();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao criar empresa');
    }
  };

  const handleTestEmail = async () => {
    try {
      // Testar a configuração SMTP com os dados do formulário
      const testResult = await api.post('/email/test-smtp-config', {
        smtpHost: formData.smtpHost,
        smtpPort: formData.smtpPort,
        smtpUser: formData.smtpUser,
        smtpPassword: formData.smtpPassword,
        smtpSecure: formData.smtpSecure,
        smtpFromName: formData.smtpFromName || formData.name,
        smtpFromEmail: formData.smtpFromEmail || formData.smtpUser,
      });
      
      if (testResult.data.success) {
        toast.success('Configuração de email testada com sucesso! Email de teste enviado.');
      } else {
        toast.error(`Erro no teste: ${testResult.data.message}`);
      }
      
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao testar configuração de email');
    }
  };

  const handleUpdateCompany = async () => {
    if (!selectedCompany) return;
    
    try {
      const updateData: UpdateCompanyData = {};
      if (formData.name) updateData.name = formData.name;
      if (formData.document) updateData.document = formData.document;
      if (formData.coderp !== undefined) updateData.coderp = formData.coderp;
      if (formData.email) updateData.email = formData.email;
      if (formData.phone) updateData.phone = formData.phone;
      if (formData.address) updateData.address = formData.address;
      // Configuração de Email
      if (formData.smtpHost !== undefined) updateData.smtpHost = formData.smtpHost;
      if (formData.smtpPort !== undefined) updateData.smtpPort = formData.smtpPort;
      if (formData.smtpUser !== undefined) updateData.smtpUser = formData.smtpUser;
      if (formData.smtpPassword !== undefined) updateData.smtpPassword = formData.smtpPassword;
      if (formData.smtpSecure !== undefined) updateData.smtpSecure = formData.smtpSecure;
      if (formData.smtpFromName !== undefined) updateData.smtpFromName = formData.smtpFromName;
      if (formData.smtpFromEmail !== undefined) updateData.smtpFromEmail = formData.smtpFromEmail;
      if (formData.isDefault !== undefined) updateData.isDefault = formData.isDefault;

      await api.put(`/companies/${selectedCompany.id}`, updateData);
      toast.success('Empresa atualizada com sucesso!');
      setShowEditModal(false);
      setSelectedCompany(null);
      setFormData({ 
        name: '', 
        document: '', 
        coderp: '',
        email: '', 
        phone: '', 
        address: '',
        smtpHost: '',
        smtpPort: 587,
        smtpUser: '',
        smtpPassword: '',
        smtpSecure: false,
        smtpFromName: '',
        smtpFromEmail: '',
        isDefault: false,
      });
      loadCompanies();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao atualizar empresa');
    }
  };



  const handleInactivateCompany = async (companyId: string) => {
    if (!confirm('Tem certeza que deseja inativar esta empresa?\n\n⚠️ Empresas inativas não aparecerão em listas ativas, mas os dados serão preservados.')) return;
    
    try {
      await api.patch(`/companies/${companyId}/inactivate`);
      toast.success('Empresa inativada com sucesso');
      loadCompanies();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao inativar empresa');
    }
  };

  const handleActivateCompany = async (companyId: string) => {
    try {
      await api.patch(`/companies/${companyId}/activate`);
      toast.success('Empresa ativada com sucesso');
      loadCompanies();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Erro ao ativar empresa');
    }
  };

  const handleDeleteCompany = async (companyId: string) => {
    if (!confirm('Tem certeza que deseja excluir esta empresa?\n\n⚠️ Esta ação é irreversível e só é permitida para empresas sem títulos.')) return;
    
    try {
      await api.delete(`/companies/${companyId}`);
      toast.success('Empresa excluída com sucesso');
      loadCompanies();
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Erro ao excluir empresa';
      
      // Se for erro 400, mostrar mensagem mais detalhada
      if (error.response?.status === 400) {
        toast.error(`❌ ${errorMessage}\n\n💡 Dica: Use a opção "Inativar" em vez de "Deletar" para empresas com dados.`);
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const openEditModal = (company: Company) => {
    setSelectedCompany(company);
    setFormData({
      name: company.name,
      document: company.document,
      coderp: company.coderp || '',
      email: company.email,
      phone: company.phone,
      address: company.address,
      // Configuração de Email
      smtpHost: company.smtpHost || '',
      smtpPort: company.smtpPort || 587,
      smtpUser: company.smtpUser || '',
      smtpPassword: company.smtpPassword || '',
      smtpSecure: company.smtpSecure || false,
      smtpFromName: company.smtpFromName || '',
      smtpFromEmail: company.smtpFromEmail || '',
      isDefault: company.isDefault || false,
    });
    setShowEditModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg">Carregando empresas...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 md:space-y-8" data-testid="page-empresas">
      <div className="flex flex-col gap-4 sm:flex-row sm:justify-between sm:items-center">
        <div>
          <h1 className="font-heading text-heading-1 font-bold text-foreground">Gerenciamento de Empresas</h1>
          <p className="font-body text-body text-muted-foreground mt-1">Gerencie as empresas do sistema</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Empresa
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Lista de Empresas</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>CNPJ/CPF</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Telefone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Padrão</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {companies.map((company) => (
                <TableRow 
                  key={company.id}
                  className={!company.isActive ? 'bg-gray-50 dark:bg-slate-800 opacity-75' : ''}
                >
                  <TableCell className="font-medium">{company.name}</TableCell>
                  <TableCell>{formatCNPJ(company.document)}</TableCell>
                  <TableCell>{company.email}</TableCell>
                  <TableCell>{formatPhone(company.phone)}</TableCell>
                  <TableCell>
                    <Badge className={company.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {company.isActive ? 'Ativa' : 'Inativa'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {company.isDefault ? (
                      <Badge className="bg-blue-100 text-blue-800">
                        Padrão
                      </Badge>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(company.createdAt).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditModal(company)}
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      
                      {company.isActive ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleInactivateCompany(company.id)}
                          title="Inativar Empresa"
                          className="text-orange-600 hover:text-orange-700"
                        >
                          <EyeOff className="h-4 w-4" />
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleActivateCompany(company.id)}
                          title="Ativar Empresa"
                          className="text-green-600 hover:text-green-700"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteCompany(company.id)}
                        title="Deletar Empresa"
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Company Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Criar Nova Empresa"
      >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general" className="flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              Geral
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Nome da Empresa</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome da empresa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">CNPJ/CPF</label>
              <Input
                value={formData.document}
                onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                placeholder="00.000.000/0000-00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Código ERP</label>
              <Input
                value={formData.coderp || ''}
                onChange={(e) => setFormData({ ...formData, coderp: e.target.value })}
                placeholder="Código da empresa no ERP"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contato@empresa.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Telefone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Endereço</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Rua, número - Cidade/UF"
              />
            </div>
          </TabsContent>

          <TabsContent value="email" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Servidor SMTP</label>
                <Input
                  value={formData.smtpHost}
                  onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Porta</label>
                <Input
                  type="number"
                  value={formData.smtpPort}
                  onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) || 587 })}
                  placeholder="587"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Usuário</label>
                <Input
                  value={formData.smtpUser}
                  onChange={(e) => setFormData({ ...formData, smtpUser: e.target.value })}
                  placeholder="seu-email@gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Senha</label>
                <Input
                  type="password"
                  value={formData.smtpPassword}
                  onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
                  placeholder="Sua senha"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Nome do Remetente</label>
                <Input
                  value={formData.smtpFromName}
                  onChange={(e) => setFormData({ ...formData, smtpFromName: e.target.value })}
                  placeholder="Nome da Empresa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Email do Remetente</label>
                <Input
                  type="email"
                  value={formData.smtpFromEmail}
                  onChange={(e) => setFormData({ ...formData, smtpFromEmail: e.target.value })}
                  placeholder="noreply@empresa.com"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="smtpSecure"
                checked={formData.smtpSecure}
                onChange={(e) => setFormData({ ...formData, smtpSecure: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="smtpSecure" className="text-sm font-medium">
                Usar SSL/TLS
              </label>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isDefault"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="isDefault" className="text-sm font-medium">
                Empresa Padrão (usar credenciais para testes de email)
              </label>
            </div>
            
            <div className="pt-4">
              <Button 
                type="button"
                variant="outline"
                onClick={handleTestEmail}
                className="w-full"
                disabled={!formData.smtpHost || !formData.smtpUser || !formData.smtpPassword}
              >
                <Mail className="w-4 h-4 mr-2" />
                Testar Configuração de Email
              </Button>
            </div>
          </TabsContent>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateCompany}>
              Criar Empresa
            </Button>
          </div>
        </Tabs>
      </Modal>

      {/* Edit Company Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Editar Empresa"
      >
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="general" className="flex items-center">
              <Settings className="w-4 h-4 mr-2" />
              Geral
            </TabsTrigger>
            <TabsTrigger value="email" className="flex items-center">
              <Mail className="w-4 h-4 mr-2" />
              Email
            </TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Nome da Empresa</label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Nome da empresa"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">CNPJ/CPF</label>
              <Input
                value={formData.document}
                onChange={(e) => setFormData({ ...formData, document: e.target.value })}
                placeholder="00.000.000/0000-00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Email</label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="contato@empresa.com"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Telefone</label>
              <Input
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder="(11) 99999-9999"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Endereço</label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Rua, número - Cidade/UF"
              />
            </div>
          </TabsContent>

          <TabsContent value="email" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Servidor SMTP</label>
                <Input
                  value={formData.smtpHost}
                  onChange={(e) => setFormData({ ...formData, smtpHost: e.target.value })}
                  placeholder="smtp.gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Porta</label>
                <Input
                  type="number"
                  value={formData.smtpPort}
                  onChange={(e) => setFormData({ ...formData, smtpPort: parseInt(e.target.value) || 587 })}
                  placeholder="587"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Usuário</label>
                <Input
                  value={formData.smtpUser}
                  onChange={(e) => setFormData({ ...formData, smtpUser: e.target.value })}
                  placeholder="seu-email@gmail.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Senha</label>
                <Input
                  type="password"
                  value={formData.smtpPassword}
                  onChange={(e) => setFormData({ ...formData, smtpPassword: e.target.value })}
                  placeholder="Sua senha"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Nome do Remetente</label>
                <Input
                  value={formData.smtpFromName}
                  onChange={(e) => setFormData({ ...formData, smtpFromName: e.target.value })}
                  placeholder="Nome da Empresa"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-200 mb-1">Email do Remetente</label>
                <Input
                  type="email"
                  value={formData.smtpFromEmail}
                  onChange={(e) => setFormData({ ...formData, smtpFromEmail: e.target.value })}
                  placeholder="noreply@empresa.com"
                />
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="smtpSecureEdit"
                checked={formData.smtpSecure}
                onChange={(e) => setFormData({ ...formData, smtpSecure: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="smtpSecureEdit" className="text-sm font-medium">
                Usar SSL/TLS
              </label>
            </div>
            
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="isDefaultEdit"
                checked={formData.isDefault}
                onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                className="rounded border-gray-300"
              />
              <label htmlFor="isDefaultEdit" className="text-sm font-medium">
                Empresa Padrão (usar credenciais para testes de email)
              </label>
            </div>
            
            <div className="pt-4">
              <Button 
                type="button"
                variant="outline"
                onClick={handleTestEmail}
                className="w-full"
                disabled={!formData.smtpHost || !formData.smtpUser || !formData.smtpPassword}
              >
                <Mail className="w-4 h-4 mr-2" />
                Testar Configuração de Email
              </Button>
            </div>
          </TabsContent>

          <div className="flex justify-end space-x-2 pt-4">
            <Button variant="outline" onClick={() => setShowEditModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleUpdateCompany}>
              Atualizar Empresa
            </Button>
          </div>
        </Tabs>
      </Modal>
    </div>
  );
};
